/**
 * simAlumnosStore.js — alumnos que nacen DURANTE una demo del simulador de autoescuelas y el alumno de
 * prueba de David.
 *
 * La lista compartida es SECTOR_DATA_MAP.autoescuela.mockAlumnos (la leen Alumnos, la ficha, Chats,
 * Planificación…): aquí solo se le añaden/quitan elementos IN SITU (nunca se reasigna el array, que
 * varias pantallas guardan por referencia).
 *
 * Marcas en el alumno:
 *  - sim_demo_created: creado en esta demo (buscador «Crear alumno», Alumnos → Nuevo, Chats, Importar).
 *    Se borran al salir del simulador (resetSimDemoSession, desde SimulatorContext.deactivate).
 *  - sim_real_phone: su teléfono es DE VERDAD (lo tecleó el closer) → los avisos de «Notificar de forma
 *    individual» y «Enviar semana» van a SU número, sin preguntar el móvil del lead. Los alumnos de
 *    ejemplo (+34611100001…) no lo llevan: para ellos se sigue preguntando el móvil del lead.
 */
import { SECTOR_DATA_MAP } from "../../mockData";
import { normalizeLeadPhone, setLeadPhone } from "./leadPhoneStore";
import { resetProfesoresDemo } from "./profesoresStore";
import { reset as resetReservaDemo } from "./reservaDemoStore";
import { resetProfesorConfig } from "./profesor/profesorConfigStore";
import { resetImpedimentos } from "./profesor/impedimentosStore";

/** Alumno de prueba de David: está SIEMPRE (no se borra al salir) para hacerse demos a sí mismo. */
export const DAVID_ALUMNO_ID = "alu-david-prueba";
export const DAVID_ALUMNO = Object.freeze({
  alumno_id: DAVID_ALUMNO_ID,
  name: "David (prueba Conext)",
  status: "activo",
  phone: "+34686851695",
  dni: "",
  carnet_type: "B",
  horas_acumuladas: 0,
  practicas_completadas: 0,
  fecha_nacimiento: "",
  observaciones: "Alumno de prueba de Conext: los avisos le llegan de verdad a este móvil.",
  sim_real_phone: true,
  // Profesor habitual: el de la demo (PROFESOR_DEMO_ID de reservaDemoStore), el mismo que llevan sus prácticas reales.
  instructor_id: "inst-001",
});

const lista = () => {
  const data = SECTOR_DATA_MAP.autoescuela;
  if (!data) return [];
  if (!Array.isArray(data.mockAlumnos)) data.mockAlumnos = [];
  return data.mockAlumnos;
};

/** Teléfono E.164 al que mandar el aviso real de este alumno, o "" si es un alumno de ejemplo. */
export function realPhoneOf(alumno) {
  if (!alumno || alumno.sim_real_phone !== true) return "";
  return normalizeLeadPhone(alumno.phone);
}

export const isRealPhoneAlumno = (alumno) => !!realPhoneOf(alumno);

let seq = 0;

/** Marca un alumno recién creado en la demo (teléfono real si lo trae). */
export function markDemoAlumno(alumno, { realPhone = true } = {}) {
  return {
    ...alumno,
    sim_demo_created: true,
    ...(realPhone && normalizeLeadPhone(alumno?.phone) ? { sim_real_phone: true } : {}),
  };
}

/** Añade (o actualiza, si ya está) un alumno creado en la demo a la lista compartida. Devuelve el guardado. */
export function registerDemoAlumno(alumno, opts) {
  const marked = markDemoAlumno(alumno, opts);
  const l = lista();
  const i = l.findIndex((a) => a.alumno_id === marked.alumno_id);
  if (i >= 0) Object.assign(l[i], marked);
  else l.push(marked);
  return i >= 0 ? l[i] : marked;
}

/** Alta rápida desde el buscador de Planificación: nombre + teléfono (E.164) y, con sedes, su sede. */
export function addDemoAlumno({ name, phone, sede_id } = {}) {
  seq += 1;
  return registerDemoAlumno({
    alumno_id: `alu-demo-${Date.now().toString(36)}-${seq}`,
    name: String(name || "").trim(),
    phone: normalizeLeadPhone(phone) || "",
    status: "activo",
    dni: "",
    carnet_type: "B",
    horas_acumuladas: 0,
    practicas_completadas: 0,
    fecha_nacimiento: "",
    observaciones: "",
    ...(sede_id ? { sede_id } : {}),
  });
}

/** Quita los alumnos creados en la demo y asegura que el de prueba de David sigue ahí (y vuelve a ser David). */
export function resetDemoAlumnos() {
  const l = lista();
  for (let i = l.length - 1; i >= 0; i -= 1) {
    if (l[i] && l[i].sim_demo_created === true && l[i].alumno_id !== DAVID_ALUMNO_ID) l.splice(i, 1);
  }
  const david = l.find((a) => a.alumno_id === DAVID_ALUMNO_ID);
  if (!david) l.push({ ...DAVID_ALUMNO });
  else volverADavid(david);
}

function volverADavid(alumno) {
  const { sim_lead, ...resto } = alumno;
  if (sim_lead !== undefined) delete alumno.sim_lead;
  if (resto.name !== DAVID_ALUMNO.name || resto.phone !== DAVID_ALUMNO.phone || resto.sim_real_phone !== true || resto.observaciones !== DAVID_ALUMNO.observaciones || resto.instructor_id !== DAVID_ALUMNO.instructor_id) {
    Object.assign(alumno, { name: DAVID_ALUMNO.name, phone: DAVID_ALUMNO.phone, observaciones: DAVID_ALUMNO.observaciones, sim_real_phone: true, instructor_id: DAVID_ALUMNO.instructor_id });
  }
}

/**
 * El alumno por defecto es EL LEAD (6/10/2026): al entrar al simulador de autoescuelas desde su ficha, el alumno
 * de prueba (`alu-david-prueba`) pasa a llevar su nombre y su móvil, así los avisos reales y la reserva en el móvil
 * le llegan a él. Sin un móvil válido del lead se queda el de David. Muta IN SITU (nunca reasigna el array: varias
 * pantallas lo guardan por referencia). `resetDemoAlumnos` (al salir) lo devuelve a David.
 */
export function aplicarLeadComoAlumno({ contactName, personaContactoNombre, contactPhone, personaContactoPhone } = {}) {
  const l = lista();
  let alumno = l.find((a) => a.alumno_id === DAVID_ALUMNO_ID);
  if (!alumno) { alumno = { ...DAVID_ALUMNO }; l.push(alumno); }
  const phone = normalizeLeadPhone(contactPhone) || normalizeLeadPhone(personaContactoPhone);
  if (!phone) { volverADavid(alumno); return alumno; }
  const name = String(personaContactoNombre || contactName || "").trim() || DAVID_ALUMNO.name;
  Object.assign(alumno, {
    name,
    phone,
    sim_real_phone: true,
    sim_lead: true,
    observaciones: "El lead de esta demo: los avisos le llegan de verdad a su móvil.",
  });
  return alumno;
}

/**
 * Al salir del simulador: la siguiente demo empieza limpia (sin altas de esta, sin el móvil del lead, con
 * los profesores y horarios de fábrica y sin seguir la reserva en el móvil del lead).
 */
export function resetSimDemoSession() {
  resetDemoAlumnos();
  setLeadPhone(null);
  resetProfesoresDemo();
  // La reserva en el móvil del lead: deja de consultar su estado y olvida lo de esta demo.
  resetReservaDemo();
  // Avisos a profesores (Semana a profesores, horario de mañana, plazo) y los impedimentos que mandaron.
  resetProfesorConfig();
  resetImpedimentos();
}
